import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MENU } from './Sidebar';
import { useAuth } from '../../context/AuthContext';
import notificacaoService from '../../services/notificacao.service';

// Busca o label da rota atual para exibir no título do topbar
function getTituloRota(pathname) {
  for (const grupo of MENU) {
    for (const item of grupo.itens) {
      if (item.path === pathname) return item.label;
    }
  }
  return 'Portal';
}

const S = {
  topbar: (sidebarWidth) => ({
    position: 'fixed',
    top: 0,
    left: sidebarWidth,
    right: 0,
    height: '52px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e8eaed',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    gap: '12px',
    zIndex: 99,
    transition: 'left 0.25s ease',
    fontFamily: "'Manrope', sans-serif",
  }),
  btnToggle: {
    width: '32px', height: '32px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#666',
    flexShrink: 0,
  },
  titulo: {
    fontSize: '15px', fontWeight: '500',
    color: '#1a1a2e',
    flex: 1,
  },
  actions: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  iconBtn: {
    width: '32px', height: '32px',
    border: '1px solid #e8eaed',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#666',
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: '-4px', right: '-4px',
    backgroundColor: '#e53935',
    color: '#fff',
    fontSize: '9px',
    minWidth: '15px', height: '15px', padding: '0 3px',
    borderRadius: '50px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: '700',
    boxSizing: 'border-box',
  },
  dropdown: {
    position: 'absolute',
    top: '40px', right: 0,
    width: '380px', maxHeight: '480px',
    backgroundColor: '#fff',
    border: '1px solid #e8eaed',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 200,
    display: 'flex', flexDirection: 'column',
  },
  dropdownHeader: {
    padding: '12px 14px',
    borderBottom: '1px solid #e8eaed',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    fontSize: '13px', fontWeight: '700', color: '#1a1a2e',
  },
  marcarTodas: {
    fontSize: '11px', fontWeight: '600',
    color: '#003399', cursor: 'pointer',
    border: 'none', background: 'transparent',
    padding: 0,
  },
  lista: {
    overflowY: 'auto', flex: 1,
  },
  item: (lida) => ({
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    backgroundColor: lida ? '#fff' : '#eff6ff',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#1a1a2e',
  }),
  itemTitulo: (lida) => ({
    fontWeight: lida ? '500' : '700',
    fontSize: '13px',
    marginBottom: '3px',
    color: '#1a1a2e',
  }),
  itemMsg: {
    color: '#666',
    lineHeight: '1.4',
    marginBottom: '4px',
  },
  itemData: {
    color: '#888',
    fontSize: '11px',
  },
  vazio: {
    padding: '30px 14px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
    fontStyle: 'italic',
  },
};

function formatarDataRelativa(d) {
  if (!d) return '';
  const data = new Date(d);
  const agora = new Date();
  const diffMs = agora - data;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHora = Math.floor(diffMs / 3600000);
  const diffDia = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin} min atrás`;
  if (diffHora < 24) return `${diffHora}h atrás`;
  if (diffDia < 7) return `${diffDia}d atrás`;
  return data.toLocaleDateString('pt-BR');
}

function Topbar({ collapsed, onToggle }) {
  const location = useLocation();
  const { usuario } = useAuth();
  const titulo = getTituloRota(location.pathname);
  const sidebarWidth = collapsed ? '52px' : '220px';

  const [contagem, setContagem]   = useState(0);
  const [aberto, setAberto]       = useState(false);
  const [notifs, setNotifs]       = useState([]);
  const [carregando, setCarregando] = useState(false);
  const dropdownRef = useRef(null);

  // Atualiza contagem do badge (polling a cada 30s)
  const atualizarContagem = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      const qtd = await notificacaoService.contagem(usuario.id);
      setContagem(qtd);
    } catch (err) {
      // Silencioso — tabela pode não existir ainda; só não mostra badge
      console.debug('Notif contagem:', err.message);
    }
  }, [usuario]);

  useEffect(() => {
    atualizarContagem();
    const intv = setInterval(atualizarContagem, 30000);
    return () => clearInterval(intv);
  }, [atualizarContagem]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aberto]);

  const abrirDropdown = async () => {
    if (aberto) { setAberto(false); return; }
    setAberto(true);
    if (!usuario?.id) return;
    setCarregando(true);
    try {
      const lista = await notificacaoService.listar(usuario.id);
      setNotifs(lista);
    } catch (err) {
      console.warn('Notif listar:', err.message);
      setNotifs([]);
    } finally {
      setCarregando(false);
    }
  };

  const clicarItem = async (n) => {
    if (n.lida_em) return;  // já estava lida
    try {
      await notificacaoService.marcarLida(n.id, usuario.id);
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, lida_em: new Date().toISOString() } : x));
      setContagem(c => Math.max(0, c - 1));
    } catch (err) {
      console.warn('Marcar lida:', err.message);
    }
  };

  const marcarTodas = async () => {
    if (!usuario?.id || contagem === 0) return;
    try {
      await notificacaoService.marcarTodasLidas(usuario.id);
      const agora = new Date().toISOString();
      setNotifs(prev => prev.map(x => x.lida_em ? x : { ...x, lida_em: agora }));
      setContagem(0);
    } catch (err) {
      console.warn('Marcar todas:', err.message);
    }
  };

  return (
    <div style={S.topbar(sidebarWidth)}>
      {/* Botão recolher/expandir menu */}
      <button style={S.btnToggle} onClick={onToggle} title={collapsed ? 'Expandir menu' : 'Recolher menu'}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Título da página atual */}
      <span style={S.titulo}>{titulo}</span>

      {/* Ações do topo */}
      <div style={S.actions}>
        {/* Notificações */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button style={S.iconBtn} title="Notificações" onClick={abrirDropdown}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2a5 5 0 00-5 5v2L1.5 11.5h13L13 9V7a5 5 0 00-5-5zM6.5 13a1.5 1.5 0 003 0"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {contagem > 0 && (
              <span style={S.badge}>{contagem > 99 ? '99+' : contagem}</span>
            )}
          </button>

          {aberto && (
            <div style={S.dropdown}>
              <div style={S.dropdownHeader}>
                <span>Notificações</span>
                {contagem > 0 && (
                  <button style={S.marcarTodas} onClick={marcarTodas}>
                    Marcar todas como lidas
                  </button>
                )}
              </div>
              <div style={S.lista}>
                {carregando ? (
                  <div style={S.vazio}>Carregando...</div>
                ) : notifs.length === 0 ? (
                  <div style={S.vazio}>Sem notificações.</div>
                ) : notifs.map(n => (
                  <div key={n.id} style={S.item(!!n.lida_em)} onClick={() => clicarItem(n)}>
                    <div style={S.itemTitulo(!!n.lida_em)}>{n.titulo}</div>
                    {n.mensagem && <div style={S.itemMsg}>{n.mensagem}</div>}
                    <div style={S.itemData}>{formatarDataRelativa(n.criado_em)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Perfil */}
        <button style={S.iconBtn} title="Meu perfil">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 14c0-3 2.7-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

export default Topbar;
